import { useEffect, useMemo, useState } from "react";
import ReportHelpCard from "../components/reports/ReportHelpCard";
import ReportPageHeader from "../components/reports/ReportPageHeader";
import { useAuth } from "../context/AuthContext";
import { fetchSuperAdminBillingCouponReport } from "../api/superadmin";
import {
  BTN_OUTLINE,
  BTN_PRIMARY,
  FIELD_CLASS,
  LABEL_TEXT,
  MODAL_TEXT,
} from "../ui/modalStyles";
import "./ReportsSectionCompact.css";

const PAGE_SIZES = [25, 50, 100, 200];

function defaultFilters() {
  const today = new Date();
  const end = today.toISOString().slice(0, 10);
  const start = new Date(today);
  start.setDate(today.getDate() - 29);
  return {
    start_date: start.toISOString().slice(0, 10),
    end_date: end,
    status: "all",
    search: "",
    page: 1,
    page_size: 50,
  };
}

function SummaryCard({ label, value, hint }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
      <div className="text-xs font-semibold uppercase text-slate-500">{label}</div>
      <div className="mt-2 text-2xl font-semibold text-slate-900">{value ?? 0}</div>
      {hint ? <div className="mt-1 text-xs text-slate-500">{hint}</div> : null}
    </div>
  );
}

function formatDateTime(value) {
  if (!value) return "--";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleString("en-CA", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function titleCase(value) {
  return String(value || "")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function durationLabel(row) {
  if (row.duration === "once") return "Once";
  if (row.duration === "forever") return "Forever";
  const months = Number(row.duration_in_months || 0);
  return months ? `${months} months` : "Repeating";
}

export default function SuperAdminBillingCouponReport() {
  const { is_super_admin } = useAuth();
  const [filters, setFilters] = useState(defaultFilters);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const totalPages = data?.total_pages || 1;
  const couponRows = data?.by_coupon || [];
  const redemptionRows = data?.redemptions || [];
  const queryFilters = useMemo(() => filters, [filters]);

  const loadData = async (nextFilters = queryFilters) => {
    setLoading(true);
    setError("");
    try {
      const payload = await fetchSuperAdminBillingCouponReport(nextFilters);
      setData(payload);
      setFilters((current) => ({
        ...current,
        start_date: payload?.filters?.start_date || current.start_date,
        end_date: payload?.filters?.end_date || current.end_date,
      }));
    } catch (err) {
      setError(err?.message || "Unable to load billing coupon report.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (is_super_admin) loadData(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [is_super_admin]);

  const updateFilter = (key, value) => {
    setFilters((current) => ({
      ...current,
      [key]: value,
      page: key === "page" ? value : 1,
    }));
  };

  const clearFilters = () => {
    const next = defaultFilters();
    setFilters(next);
    loadData(next);
  };

  const goToPage = (page) => {
    const safePage = Math.max(1, Math.min(Number(page || 1), totalPages));
    const next = { ...filters, page: safePage };
    setFilters(next);
    loadData(next);
  };

  if (!is_super_admin) {
    return (
      <div className="reportSectionShell min-h-screen bg-slate-100">
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          Super Admin access required.
        </div>
      </div>
    );
  }

  const summary = data?.summary || {};

  return (
    <div className="reportSectionShell min-h-screen bg-slate-100">
      <div className="reportSectionWide report-section-spacious report-section-compact">
        <div className="overflow-hidden rounded-3xl bg-white shadow-2xl">
          <ReportPageHeader title="Billing Coupon Report" closeTo="/superadmin/reports" />
          <div className={`space-y-6 px-6 py-6 ${MODAL_TEXT}`}>
            <ReportHelpCard
              title="Help Guide: Billing Coupon Report"
              subtitle="Review discount-code activity by campaign, salon, business, and redemption status."
              sections={[
                {
                  id: "tracking",
                  title: "Track coupon campaigns",
                  description:
                    "Use this report to see which Ellyanna billing coupons are being used and which salons used them.",
                  anchorId: "billing-coupon-report-results",
                  bullets: [
                    "Date filters use the salon-local redemption date recorded when the salon applied the code.",
                    "Shared Super Admin location filters above the report can narrow results by salon or business location.",
                    "Use one coupon code per campaign so campaign success is easier to measure.",
                  ],
                },
              ]}
              troubleshooting={[
                {
                  title: "A coupon is missing from this report",
                  body: "Only coupons with redemptions in the selected date range appear in the campaign rows. The Billing Coupons page still shows all created codes.",
                },
              ]}
            />

            {error ? (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {error}
              </div>
            ) : null}

            <div className="rounded-2xl border border-slate-200 bg-white p-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
                <div>
                  <label className={LABEL_TEXT}>Start date</label>
                  <input
                    className={`${FIELD_CLASS} w-full`}
                    type="date"
                    value={filters.start_date}
                    onChange={(event) => updateFilter("start_date", event.target.value)}
                  />
                </div>
                <div>
                  <label className={LABEL_TEXT}>End date</label>
                  <input
                    className={`${FIELD_CLASS} w-full`}
                    type="date"
                    value={filters.end_date}
                    onChange={(event) => updateFilter("end_date", event.target.value)}
                  />
                </div>
                <div>
                  <label className={LABEL_TEXT}>Status</label>
                  <select
                    className={`${FIELD_CLASS} w-full`}
                    value={filters.status}
                    onChange={(event) => updateFilter("status", event.target.value)}
                  >
                    <option value="all">All</option>
                    <option value="applied">Applied</option>
                    <option value="removed">Removed</option>
                    <option value="expired">Expired</option>
                    <option value="failed">Failed</option>
                  </select>
                </div>
                <div>
                  <label className={LABEL_TEXT}>Rows</label>
                  <select
                    className={`${FIELD_CLASS} w-full`}
                    value={filters.page_size}
                    onChange={(event) => updateFilter("page_size", Number(event.target.value))}
                  >
                    {PAGE_SIZES.map((size) => (
                      <option key={size} value={size}>{size}</option>
                    ))}
                  </select>
                </div>
                <div className="xl:col-span-1">
                  <label className={LABEL_TEXT}>Search</label>
                  <input
                    className={`${FIELD_CLASS} w-full`}
                    value={filters.search}
                    onChange={(event) => updateFilter("search", event.target.value)}
                    placeholder="Code, campaign, salon"
                  />
                </div>
              </div>
              <div className="mt-4 flex justify-end gap-3">
                <button type="button" className={BTN_OUTLINE} onClick={clearFilters} disabled={loading}>
                  Clear
                </button>
                <button type="button" className={BTN_PRIMARY} onClick={() => loadData(filters)} disabled={loading}>
                  {loading ? "Running..." : "Run Report"}
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
              <SummaryCard label="Redemptions" value={summary.redemptions} />
              <SummaryCard label="Active redemptions" value={summary.active_redemptions} />
              <SummaryCard label="Removed" value={summary.removed_redemptions} />
              <SummaryCard label="Businesses" value={summary.unique_businesses} />
              <SummaryCard label="Active coupons" value={summary.active_coupons} hint={`${summary.total_coupons || 0} total`} />
            </div>

            <div id="billing-coupon-report-results" className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
              <div className="border-b border-slate-100 px-4 py-3 text-sm font-semibold text-slate-900">
                Campaign Summary
              </div>
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-xs font-semibold uppercase text-slate-500">
                  <tr>
                    <th className="px-3 py-2 text-left">Code</th>
                    <th className="px-3 py-2 text-left">Campaign</th>
                    <th className="px-3 py-2 text-left">Discount</th>
                    <th className="px-3 py-2 text-left">Usage</th>
                    <th className="px-3 py-2 text-left">Date Range</th>
                    <th className="px-3 py-2 text-left">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {loading ? (
                    <tr><td className="px-3 py-4 text-slate-500" colSpan="6">Loading coupon campaigns...</td></tr>
                  ) : couponRows.length === 0 ? (
                    <tr><td className="px-3 py-4 text-slate-500" colSpan="6">No coupon redemptions found for this date range.</td></tr>
                  ) : (
                    couponRows.map((row) => (
                      <tr key={row.coupon_id} className="align-top hover:bg-slate-50">
                        <td className="px-3 py-3 font-semibold text-slate-900">{row.code}</td>
                        <td className="px-3 py-3 text-slate-700">{row.campaign_name || "--"}</td>
                        <td className="px-3 py-3 text-slate-700">
                          <div>{row.percent_off}% off</div>
                          <div className="text-xs text-slate-500">{durationLabel(row)}</div>
                        </td>
                        <td className="px-3 py-3 text-slate-700">
                          <div>{row.redemptions}{row.max_redemptions ? ` / ${row.max_redemptions}` : ""} used</div>
                          <div className="text-xs text-slate-500">{row.businesses} businesses</div>
                        </td>
                        <td className="px-3 py-3 text-slate-700">
                          <div>{row.first_applied_date || "--"} to {row.last_applied_date || "--"}</div>
                          <div className="text-xs text-slate-500">Salon-local redemption dates</div>
                        </td>
                        <td className="px-3 py-3">
                          <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${row.is_active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                            {row.is_active ? "Active" : "Inactive"}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
              <div className="border-b border-slate-100 px-4 py-3 text-sm font-semibold text-slate-900">
                Redemption Details
              </div>
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-xs font-semibold uppercase text-slate-500">
                  <tr>
                    <th className="px-3 py-2 text-left">Applied</th>
                    <th className="px-3 py-2 text-left">Code</th>
                    <th className="px-3 py-2 text-left">Salon</th>
                    <th className="px-3 py-2 text-left">Business</th>
                    <th className="px-3 py-2 text-left">Applied By</th>
                    <th className="px-3 py-2 text-left">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {loading ? (
                    <tr><td className="px-3 py-4 text-slate-500" colSpan="6">Loading redemptions...</td></tr>
                  ) : redemptionRows.length === 0 ? (
                    <tr><td className="px-3 py-4 text-slate-500" colSpan="6">No redemption details found.</td></tr>
                  ) : (
                    redemptionRows.map((row) => (
                      <tr key={row.id} className="align-top hover:bg-slate-50">
                        <td className="px-3 py-3 text-slate-700">
                          <div>{row.applied_date_local || "--"}</div>
                          <div className="text-xs text-slate-500">{row.timezone || "Salon timezone"}</div>
                          <div className="text-xs text-slate-400">{formatDateTime(row.applied_at_utc)}</div>
                        </td>
                        <td className="px-3 py-3">
                          <div className="font-semibold text-slate-900">{row.code}</div>
                          <div className="text-xs text-slate-500">{row.campaign_name || "--"}</div>
                        </td>
                        <td className="px-3 py-3 text-slate-700">{row.salon_name || `Salon ${row.salon_id}`}</td>
                        <td className="px-3 py-3 text-slate-700">{row.business_name || `Business ${row.business_id}`}</td>
                        <td className="px-3 py-3 text-slate-700">
                          <div>{row.applied_by_name || "--"}</div>
                          {row.applied_by_email ? <div className="text-xs text-slate-500">{row.applied_by_email}</div> : null}
                        </td>
                        <td className="px-3 py-3">
                          <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700">
                            {titleCase(row.status)}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm">
              <button type="button" className={BTN_OUTLINE} onClick={() => goToPage(filters.page - 1)} disabled={loading || filters.page <= 1}>
                Previous
              </button>
              <div className="text-slate-600">Page {filters.page} of {totalPages}</div>
              <button type="button" className={BTN_OUTLINE} onClick={() => goToPage(filters.page + 1)} disabled={loading || filters.page >= totalPages}>
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
